import { trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { GalleryDto, UpdateGalleryBodyDto } from '@nusign/picflow-backend-types';
import { maxHeightIf } from '../../../animations/max-height.if.animation';
import { debounce } from '../../../lib/debounce';
import { GalleryService } from '../../../services/gallery.service';
import { calcLegacyTheme } from '../../lib/calc-legacy-theme';
import { GallerySettingsCustomizeHeaderComponent } from '../gallery-settings-customize-header/gallery-settings-customize-header.component';
import { GallerySettingsGroupComponent } from '../gallery-settings-group/gallery-settings-group.component';
import { ThemeCoverDescriptionItemComponent } from '../theme-cover-description-item/theme-cover-description-item.component';
import { ThemeCoverHeightItemComponent } from '../theme-cover-height-item/theme-cover-height-item.component';
import { ThemeCoverOverlayColorItemComponent } from '../theme-cover-overlay-color-item/theme-cover-overlay-color-item.component';
import { ThemeCoverSelectItemComponent } from '../theme-cover-select-item/theme-cover-select-item.component';
import { ThemeCoverTextPositionItemComponent } from '../theme-cover-text-position-item/theme-cover-text-position-item.component';
import { ThemeCoverTitleItemComponent } from '../theme-cover-title-item/theme-cover-title-item.component';

@Component({
  selector: 'app-theme-cover-customize',
  imports: [
    GallerySettingsCustomizeHeaderComponent,
    GallerySettingsGroupComponent,
    ThemeCoverSelectItemComponent,
    ThemeCoverHeightItemComponent,
    ThemeCoverTextPositionItemComponent,
    ThemeCoverTitleItemComponent,
    ThemeCoverDescriptionItemComponent,
    ThemeCoverOverlayColorItemComponent,
  ],
  animations: [trigger('optionsGroup', maxHeightIf(205))],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './theme-cover-customize.component.html',
  styleUrl: './theme-cover-customize.component.scss',
})
export class ThemeCoverCustomizeComponent {
  private galleryService = inject(GalleryService);
  gallery = input.required<GalleryDto>();
  theme = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return gallery.theme || calcLegacyTheme(gallery);
  });
  back = output<void>();

  openToggler = signal<string>(undefined);

  updateCoverTitle(color: string) {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          title_typography: {
            ...this.theme().cover.title_typography,
            font_color: color,
          },
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery().id, params);
    this.debouncedSaveTheme();
  }

  updateCoverParagraph(color: string) {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          paragraph_typography: {
            ...this.theme().cover.paragraph_typography,
            font_color: color,
          },
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery().id, params);
    this.debouncedSaveTheme();
  }

  updateCoverOverlay(color: string) {
    const params: UpdateGalleryBodyDto = {
      theme: {
        ...this.theme(),
        cover: {
          ...this.theme().cover,
          overlay_color: color,
        },
      },
    };
    this.galleryService.optimisticUpdateGallery(this.gallery().id, params);
    this.debouncedSaveTheme();
  }

  private debouncedSaveTheme = debounce(() => {
    this.galleryService.updateGallery(this.gallery().id, {
      theme: this.theme(),
    });
  }, 500);
}
